import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUI, useSettings, useEditorState } from '../contexts';
import { TreeIndexMap } from '../types';
import { scheduleLocalStorageSave } from '../utils';
import { STORAGE_FILES, STORAGE_WORKING, STORAGE_TABS, STORAGE_ACTIVE } from '../constants';

type UseFileManagerParams = {
  activeProject: string;
  treeIndex: TreeIndexMap;
  token: string | null;
  apiBase: string;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  // External state and setters for file cache
  persistedFiles: Record<string, string>;
  setPersistedFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  workingFiles: Record<string, string>;
  setWorkingFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  fetchedFiles: Record<string, boolean>;
  setFetchedFiles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  resolvePath: (id: string | null) => string;
};

export function useFileManager({
  activeProject,
  treeIndex,
  token,
  apiBase,
  authFetch,
  persistedFiles,
  setPersistedFiles,
  workingFiles,
  setWorkingFiles,
  fetchedFiles,
  setFetchedFiles,
  resolvePath,
}: UseFileManagerParams) {
  const { setStatus } = useUI();
  const { autoSaveEnabled } = useSettings();
  const { activeFile, setActiveFile, code, setCode, openTabs, setOpenTabs } = useEditorState();
  const persistTimers = useRef<Record<string, number>>({});

  const isDirty = useCallback(
    (path: string) => (workingFiles[path] ?? '') !== (persistedFiles[path] ?? ''),
    [persistedFiles, workingFiles]
  );

  const activePath = useMemo(
    () => (activeFile ? resolvePath(activeFile) : null),
    [activeFile, resolvePath]
  );

  // Persist tabs
  useEffect(() => {
    scheduleLocalStorageSave(persistTimers, STORAGE_TABS, openTabs);
  }, [openTabs]);

  // Persist files
  useEffect(() => {
    scheduleLocalStorageSave(persistTimers, `${STORAGE_FILES}:${activeProject}`, persistedFiles);
  }, [activeProject, persistedFiles]);

  // Persist working files
  useEffect(() => {
    scheduleLocalStorageSave(persistTimers, `${STORAGE_WORKING}:${activeProject}`, workingFiles);
  }, [activeProject, workingFiles]);

  // Persist active file
  useEffect(() => {
    if (activeFile) localStorage.setItem(STORAGE_ACTIVE, activeFile);
  }, [activeFile]);

  const handleSave = useCallback(async () => {
    if (!activeFile || !token || !apiBase) {
      setStatus('Kaydetmek için token yok');
      return false;
    }
    const content = workingFiles[activeFile] ?? code;
    setStatus(`Kaydediliyor · ${resolvePath(activeFile)}`);
    try {
      const res = await authFetch(`${apiBase}/flow/gitlike-save-file`, {
        method: 'POST',
        body: JSON.stringify({ projectID: activeProject, fileID: activeFile, content }),
      });
      const data = await res.json();
      if (data?.status) {
        setPersistedFiles((prev) => {
          const next = { ...prev, [activeFile]: content };
          scheduleLocalStorageSave(persistTimers, `${STORAGE_FILES}:${activeProject}`, next);
          return next;
        });
        setFetchedFiles((prev) => ({ ...prev, [activeFile]: true }));
        setStatus(`Kaydedildi · ${resolvePath(activeFile)}`);
        return true;
      }
      setStatus('Kaydetme hatası');
      return false;
    } catch (error) {
      console.error('Save failed', error);
      setStatus('Kaydetme hatası');
      return false;
    }
  }, [activeFile, activeProject, authFetch, apiBase, code, resolvePath, token, workingFiles, setStatus, setPersistedFiles, setFetchedFiles]);

  const saveCurrentIfDirty = useCallback(() => {
    if (!autoSaveEnabled || !activeFile) return;
    if (isDirty(activeFile)) {
      void handleSave();
    }
  }, [activeFile, autoSaveEnabled, handleSave, isDirty]);

  // Auto-save interval
  useEffect(() => {
    if (!autoSaveEnabled) return;
    const timer = window.setInterval(() => {
      if (activeFile && isDirty(activeFile)) {
        void handleSave();
      }
    }, 10000);
    return () => window.clearInterval(timer);
  }, [activeFile, autoSaveEnabled, handleSave, isDirty]);

  const handleOpenFile = useCallback(
    (path: string) => {
      saveCurrentIfDirty();
      setActiveFile(path);
      setOpenTabs((prev) => (prev.includes(path) ? prev : [...prev, path]));
      if (workingFiles[path] !== undefined) {
        setCode(workingFiles[path]);
      } else if (persistedFiles[path] !== undefined) {
        setCode(persistedFiles[path]);
      } else {
        setCode('');
        setStatus(`İçerik yükleniyor · ${resolvePath(path)}`);
      }
    },
    [persistedFiles, resolvePath, saveCurrentIfDirty, workingFiles, setStatus, setActiveFile, setOpenTabs, setCode]
  );

  const handleSelectTab = useCallback(
    (path: string) => {
      saveCurrentIfDirty();
      setActiveFile(path);
      if (workingFiles[path] !== undefined) {
        setCode(workingFiles[path]);
        setStatus(`Önbellek · ${resolvePath(path)}`);
      } else if (persistedFiles[path] !== undefined) {
        setCode(persistedFiles[path]);
        setStatus(`Önbellek · ${resolvePath(path)}`);
      } else {
        setCode('');
        setStatus(`İçerik yükleniyor · ${resolvePath(path)}`);
      }
    },
    [persistedFiles, resolvePath, saveCurrentIfDirty, workingFiles, setStatus, setActiveFile, setCode]
  );

  const handleCloseTab = useCallback(
    (path: string) => {
      if (isDirty(path)) {
        const proceed = window.confirm('Bu sekmede kaydedilmemiş değişiklikler var. Kapatmak istiyor musun?');
        if (!proceed) {
          setStatus('Kapatma iptal · kaydedilmemiş değişiklikler');
          return;
        }
      }
      setOpenTabs((prev) => {
        const next = prev.filter((p) => p !== path);
        const nextActive = path === activeFile ? next[next.length - 1] || null : activeFile;
        setActiveFile(nextActive);
        if (nextActive && workingFiles[nextActive] !== undefined) {
          setCode(workingFiles[nextActive]);
          setStatus(`Aktif · ${resolvePath(nextActive)}`);
        } else if (!nextActive) {
          setCode('');
          setStatus('Hiçbir dosya açık değil');
        }
        return next;
      });
    },
    [activeFile, isDirty, resolvePath, workingFiles, setStatus, setOpenTabs, setActiveFile, setCode]
  );

  const handleCodeChange = useCallback(
    (value: string) => {
      if (!activeFile) return;
      setCode(value);
      setWorkingFiles((prev) => ({ ...prev, [activeFile]: value }));
      setStatus(`Düzenleniyor · ${resolvePath(activeFile)}`);
    },
    [activeFile, resolvePath, setStatus, setCode, setWorkingFiles]
  );

  return {
    // Computed
    isDirty,
    activePath,

    // Handlers
    handleSave,
    saveCurrentIfDirty,
    handleOpenFile,
    handleSelectTab,
    handleCloseTab,
    handleCodeChange,
  };
}
